import React, { useState } from "react";
import {
  Space,
  message,
  Table,
  Button,
  Typography,
  Form,
  Input,
  Modal,
  Descriptions,
  Select,
} from "antd";
import Util from "../../Util";
import { PAYMENT_STATUS } from "./CommonUtils";

const { Title, Text, Link } = Typography;
const { TextArea } = Input;

function PaymentAuthorView() {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [sellerData, setSellerData] = useState(null);
  const [paymentData, setPaymentData] = useState([]);
  const [currentQuery, setCurrentQuery] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalRows, setModalRows] = useState([]);
  const [remarkMessage, setRemarkMessage] = useState("");
  const [newStatus, setNewStatus] = useState(PAYMENT_STATUS.PAUSED);
  const [salesDetailModalVisible, setSalesDetailModalVisible] = useState(false);
  const [salesDetailModalRow, setSalesDetailModalRow] = useState(null);
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const loadPaymentData = async (query) => {
    setLoading(true);
    const data = await Util.sendGet(
      "/api/moresheetStaff/getAuthorPaymentHistory",
      { query }
    );
    if (!data.success) {
      messageApi.error("มีข้อผิดพลาด");
      setLoading(false);
      return;
    }
    const payments = data.payments;
    const seller = data.seller;
    setSellerData(seller);
    const sheets = data.sheets;
    const sheetMap = new Map(
      sheets.map((sh) => [sh._id, { name: sh.name, price: sh.price }])
    );

    const relaventStores = data.relaventStores;
    const storesMap = new Map(relaventStores.map((row) => [row.code, row]));

    const realPayments = payments.map((row) => {
      const store = storesMap.get(row.store);
      const sheetList = row.sheetList.map((sh) => {
        return {
          _id: sh._id,
          name: sheetMap.get(sh.sheet)?.name,
          count: sh.count,
          totalPrice: sh.totalPrice,
        };
      });
      return {
        ...row,
        key: row._id,
        monthString: `${row.year}-${String(row.month).padStart(2, "0")}`,
        rawSaleAmount: row.total.toFixed(2),
        sharedAmount: (row.total * 0.6).toFixed(2),
        storeName: `${store.name[0]} - ${store.name[1]}`,
        sheetList,
      };
    });
    setPaymentData(realPayments);
    setLoading(false);
  };

  const onFinish = (values) => {
    setSelectedRowKeys([]);
    setSellerData(null);
    setPaymentData([]);
    const query = values.authorId;
    setCurrentQuery(query);
    loadPaymentData(query);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const hasSelected = selectedRowKeys.length > 0;

  const openUpdateModal = () => {
    const selectedPayments = paymentData.filter((payment) =>
      selectedRowKeys.includes(payment.key)
    );
    setModalRows(selectedPayments);
    setIsModalVisible(true);
  };

  const closeUpdateModal = () => {
    setIsModalVisible(false);
    setRemarkMessage("");
    setNewStatus(PAYMENT_STATUS.PAUSED);
  };

  const sendManualUpdateAction = async () => {
    setLoading(true);
    const result = await Util.sendPost(
      "/api/moresheetStaff/updatePaymentStatusByIds",
      {
        ids: selectedRowKeys,
        paymentStatus: newStatus,
        paymentPausedReason: remarkMessage,
      }
    );
    setLoading(false);
    if (result.success) {
      messageApi.success(`อัพเดตสถานะสำเร็จ ${result.modifiedCount} รายการ`);
      loadPaymentData(currentQuery); // Reload data
      closeUpdateModal();
      setSelectedRowKeys([]);
    } else {
      messageApi.error(
        `อัพเดตสถานะไม่สำเร็จ: ${result.message || "เกิดข้อผิดพลาด"}`
      );
    }
  };

  const showSalesDetailModal = (row) => {
    setSalesDetailModalRow(row);
    setSalesDetailModalVisible(true);
  };

  const closeSalesDetailModal = () => {
    setSalesDetailModalVisible(false);
  };

  const handleTableChange = (pagination) => {
    setTablePagination(pagination);
  };

  const COLUMNS = [
    {
      title: "เดือน",
      dataIndex: "monthString",
      key: "monthString",
      sorter: (a, b) => a.monthString.localeCompare(b.monthString),
      defaultSortOrder: "descend",
    },
    {
      title: "มหาวิทยาลัยที่ลงชีทขาย",
      dataIndex: "storeName",
      key: "storeName",
    },
    {
      title: "ยอดขาย",
      dataIndex: "rawSaleAmount",
      key: "rawSaleAmount",
      render: (text, row) => {
        return <Link onClick={() => showSalesDetailModal(row)}>{text}</Link>;
      },
    },
    {
      title: "ส่วนแบ่งรายได้ 60%",
      dataIndex: "sharedAmount",
      key: "sharedAmount",
    },
    {
      title: "สถานะ",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (text, _) => {
        if (text === PAYMENT_STATUS.SUCCESS) {
          return (
            <Text strong underline type="success">
              โอนแล้ว
            </Text>
          );
        } else if (text === PAYMENT_STATUS.PENDING) {
          return (
            <Text strong underline type="warning">
              รอโอนเงิน
            </Text>
          );
        } else if (text === PAYMENT_STATUS.PAUSED) {
          return (
            <Text strong underline type="danger">
              ไม่สำเร็จ
            </Text>
          );
        } else if (text === PAYMENT_STATUS.CARRIED_OVER) {
          return (
            <Text strong underline type="warning">
              ทบยอด
            </Text>
          );
        } else {
          return (
            <Text strong underline type="danger">
              รายได้ถูกยกเลิก
            </Text>
          );
        }
      },
    },
    {
      title: "หมายเหตุ",
      dataIndex: "paymentPausedReason",
      key: "paymentPausedReason",
    },
  ];

  return (
    <Space direction="vertical" size={12}>
      {contextHolder}
      <Form name="author_payment_form" layout="inline" onFinish={onFinish}>
        <Form.Item
          label="รหัสผู้ขาย หรือ นามปากกา"
          name="authorId"
          rules={[{ required: true, message: "Please input an Author ID!" }]}
        >
          <Input placeholder="รหัสผู้ขาย หรือ นามปากกา" allowClear />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form.Item>
      </Form>
      <Modal
        title="รายละเอียดยอดขาย"
        open={salesDetailModalVisible}
        onOk={closeSalesDetailModal}
        onCancel={closeSalesDetailModal}
        footer={[
          <Button key="ok" type="primary" onClick={closeSalesDetailModal}>
            OK
          </Button>,
        ]}
      >
        <p>
          {salesDetailModalRow?.monthString}: {salesDetailModalRow?.storeName}
        </p>
        <Table
          dataSource={salesDetailModalRow?.sheetList}
          columns={[
            { title: "ชื่อชีท", dataIndex: "name", key: "name" },
            { title: "จำนวน", dataIndex: "count", key: "count" },
            {
              title: "ราคารวม",
              dataIndex: "totalPrice",
              key: "totalPrice",
            },
          ]}
          pagination={false}
          size="small"
        />
        <p>รวม {salesDetailModalRow?.rawSaleAmount} บาท</p>
      </Modal>
      <Modal
        title="อัพเดตสถานะการจ่ายเงิน"
        open={isModalVisible}
        onOk={sendManualUpdateAction}
        onCancel={closeUpdateModal}
        okText="บันทึก"
        cancelText="ยกเลิก"
        confirmLoading={loading}
      >
        <Title level={5}>สถานะใหม่</Title>
        <Select
          value={newStatus}
          style={{ width: 120 }}
          onChange={(val) => setNewStatus(val)}
          options={[
            { value: PAYMENT_STATUS.PAUSED, label: "ไม่สำเร็จ" },
            { value: PAYMENT_STATUS.SUCCESS, label: "สำเร็จ" },
            { value: PAYMENT_STATUS.VOID, label: "ไม่จ่าย" },
            { value: PAYMENT_STATUS.PENDING, label: "รอโอนเงิน" },
          ]}
        />
        <Title level={5}>ใส่หมายเหตุ</Title>
        <TextArea
          rows={5}
          value={remarkMessage}
          onChange={(event) => {
            setRemarkMessage(event.currentTarget.value);
          }}
        ></TextArea>
        <Title level={5}>รายการที่เลือก ({modalRows.length} รายการ)</Title>
        <div style={{ width: "100%", overflowX: "scroll" }}>
          <Table
            dataSource={modalRows}
            columns={COLUMNS}
            pagination={false}
            size="small"
          />
        </div>
      </Modal>
      {loading && <div>Loading...</div>}
      {sellerData && (
        <Descriptions title="ข้อมูลผู้ขาย" bordered column={1}>
          <Descriptions.Item label="ข้อมูลธนาคาร">
            {sellerData.bankInfo}
          </Descriptions.Item>
          <Descriptions.Item label="นามปากกา">
            {sellerData.codeName}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {sellerData.email}
          </Descriptions.Item>
          <Descriptions.Item label="HTI Vendor ID">
            {sellerData.htiVendorId}
          </Descriptions.Item>
          <Descriptions.Item label="HTI Vendor Name">
            {sellerData.htiVendorName}
          </Descriptions.Item>
          <Descriptions.Item label="เลขบัตรประชาชน">
            {sellerData.peopleID}
          </Descriptions.Item>
          <Descriptions.Item label="ชื่อจริง">
            {sellerData.realName}
          </Descriptions.Item>
        </Descriptions>
      )}
      <Button type="primary" onClick={openUpdateModal} disabled={!hasSelected}>
        อัพเดตสถานะที่เลือก ({selectedRowKeys.length})
      </Button>
      {paymentData.length > 0 && (
        <Table
          rowSelection={rowSelection}
          dataSource={paymentData}
          columns={COLUMNS}
          pagination={{
            ...tablePagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100", "200"],
            total: paymentData.length,
          }}
          onChange={handleTableChange}
        />
      )}
    </Space>
  );
}

export default PaymentAuthorView;
